import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {Commentary} from "../../../api/types";
import useSWR from "swr";
import {api, HydraData} from "../../../api/apiProvider";
import urls from "../../../api/urls";
import dayjs from "dayjs";
import Comment from "./Comment";
import {Flex} from "antd";
import TextArea from "antd/lib/input/TextArea";
import {RollbackOutlined, SendOutlined} from "@ant-design/icons";
import IconClose from "../../ui/icons/IconClose";
import Roles from "../../../roles/Roles";

interface DescriptionProps {
  parentCommentId: number,
}

const addComment = async (text: string, parentId: number) => {
  return await api.post(urls.commentaries, {
    arg: {
      text: text,
      parent: `/api/commentary/commentaries/${parentId}`
    }
  })
}

export default function Comments({parentCommentId}: DescriptionProps) {
  const {t} = useTranslation()

  const [commentariesMap, setCommentariesMap] = useState<Record<string, Commentary[]>>()
  const [selectedComment, setSelectedComment] = useState<Commentary>()
  const [commentText, setCommentText] = useState<string>('')
  const [expandedCommentaries, setExpandedCommentaries] = useState<Record<number, boolean>>()

  const {
    data: commentaries,
    mutate: refreshCommentaries,
  } = useSWR<HydraData>(parentCommentId > 0 ? `${urls.commentaries}?parent.id=${parentCommentId}&t[]=manager:name&t[]=comment:count&t[]=comment:children` : null, api.get, {
    revalidateOnFocus: false
  })

  const isCommentExpanded = (id: number) => {
    return Boolean(expandedCommentaries && expandedCommentaries[id])
  }

  const collapseRecursive = (commentary: Commentary, expandedCommentaries: Record<number, boolean>) => {
    const newValues = {
      ...expandedCommentaries,
      [commentary.id]: false,
    }
    setExpandedCommentaries(newValues)

    if (commentary.children.length > 0) {
      commentary.children.map((child: Commentary) => collapseRecursive(child, newValues))
    }
  }

  const toggleComment = (commentary: Commentary) => {
    const isExpanded = Boolean(expandedCommentaries && expandedCommentaries[commentary.id])
    const newValues = {
      ...expandedCommentaries,
      [commentary.id]: !isExpanded,
    }

    setExpandedCommentaries(newValues)

    if (isExpanded && commentary.children.length > 0) {
      commentary.children.map((child: Commentary) => collapseRecursive(child, newValues))
    }
  }

  useEffect(() => {
    if (commentaries && commentaries.data) {
      const data: Record<string, Commentary[]> = {}
      for (let comment of commentaries.data) {
        const date = dayjs(comment.createdAt)

        if (!data[date.format('DD.MM.YYYY')]) {
          data[date.format('DD.MM.YYYY')] = []
        }

        data[date.format('DD.MM.YYYY')].push({
          author: comment.author,
          createdAt: comment.createdAt,
          deletedAt: comment.deletedAt,
          countChildren: comment.countChildren,
          children: comment.children,
          id: comment.id,
          ['@id']: comment['@id'],
          level: comment.level,
          text: comment.text,
          title: comment.title,
        })
      }

      setCommentariesMap(data)
    }
  }, [commentaries])

  const getDate = (date: string) => {
    const dateValue = dayjs(date, 'DD.MM.YYYY')

    if (dayjs(dateValue).isSame(dayjs(), 'day')) {
      return t('common.today')
    }

    return dateValue.format('DD MMMM YYYY')
  }

  const addNewComment = () => {
    if (!Roles.hasAccess('canAddBriefDetailsComments')) {
      return
    }

    const parentId = (selectedComment && selectedComment.id) || parentCommentId
    addComment(commentText, parentId).then(() => {
      setCommentText('')
      setSelectedComment(undefined)
      refreshCommentaries()
    })
  }

  return (
    <div className={'commentary__wrapper'}>
      <div className={'commentary__list'}>
        {commentariesMap && Object.entries(commentariesMap).map((value: [string, Commentary[]], index: number, array: [string, Commentary[]][]) => (
          <div className={'commentary__block'} key={index}>
            <div className={'commentary__date'}>{getDate(value[0])}</div>
            <div className={'commentary__items'}>
              {value[1].map((comment) => (
                <Comment
                  key={comment.id}
                  commentary={comment}
                  isCommentExpanded={isCommentExpanded}
                  toggleComment={toggleComment}
                  setSelectedComment={setSelectedComment}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <Flex className={'commentary__field-wrapper'} vertical>
        {selectedComment && (
          <Flex className={'commentary__reply'} gap={'middle'} align={'center'} justify={'space-between'}>
            <Flex gap={'middle'}>
              <RollbackOutlined/>
              <Flex vertical>
                <Flex className={'commentary__field--author'}>{selectedComment.author?.lName + ' ' + selectedComment.author?.fName}</Flex>
                <Flex>{selectedComment.text}</Flex>
              </Flex>
            </Flex>
            <div className={'commentary__reply--close-button'} onClick={() => setSelectedComment(undefined)}>
              <IconClose/>
            </div>
          </Flex>
        )}

        {Roles.hasAccess('canAddBriefDetailsComments') &&
          <Flex className={'commentary__field'} gap={'large'} align={'center'}>
            <TextArea
              value={commentText}
              onChange={(event) => {
                setCommentText(event.target.value)
              }}
              placeholder={t('edit.writeText')}
            />
            <div className={'commentary__field--button'} onClick={addNewComment}>
              <SendOutlined/>
            </div>
          </Flex>
        }
      </Flex>
    </div>
  )
}
